import { entity, persistence } from "simpler-state"
import { Option } from "@/utils"
import { INITIAL_GLOBAL_FILTER_STATE } from "@/deduction_table/table_state"
import { differenceInDays, parseISO } from "date-fns"

export * from "./active_filter_icon"
export * from "./date_range_filter"
export * from "./customer_filter"
export * from "./customer_labels"
export * from "./date_kind_toggle"
export * from "./url_sync"

export type DateKind = "invoice" | "check" | "posting" | "deposit"

export interface IGlobalFilterState {
  startDate: Option<Date>
  endDate: Option<Date>
  distributor: Option<string>
  codedFilter: string
  dateKind: DateKind
}

function deserializeFn(blob: string): IGlobalFilterState {
  let data = JSON.parse(blob)
  if (data.startDate) {
    data.startDate = parseISO(data.startDate)
  } else if (data.start_date) {
    data.startDate = parseISO(data.start_date)
  } else {
    data.startDate = INITIAL_GLOBAL_FILTER_STATE.startDate
  }
  if (data.endDate) {
    data.endDate = parseISO(data.endDate)
  } else if (data.end_date) {
    data.endDate = parseISO(data.end_date)
  } else {
    data.endDate = INITIAL_GLOBAL_FILTER_STATE.endDate
  }
  // If the persisted end date is within 27 days of today, default to today
  // This prevents users from being confused by stale time filters
  const today = new Date()
  if (data.endDate && differenceInDays(today, data.endDate) <= 27) {
    data.endDate = today
  }
  data.distributor = saved_distributor(data.distributor)
  if (!data.codedFilter) {
    data.codedFilter = INITIAL_GLOBAL_FILTER_STATE.codedFilter
  }
  if (
    !data.dateKind ||
    (data.dateKind !== "invoice" && data.dateKind !== "check" && data.dateKind !== "posting" && data.dateKind !== "deposit")
  ) {
    data.dateKind = INITIAL_GLOBAL_FILTER_STATE.dateKind
  }
  return data
}

/**
 * The customer filter as saved in the browser. A source is lowercased (older saves held "KeHE"); an
 * Other name (`other:<name>`) is kept as written, because the server matches it exactly and a
 * lowercased one matches nothing — the filter then reset itself to All Customers on every reload.
 */
export function saved_distributor(value: Option<string>): Option<string> {
  if (!value || value === "All Distributors") {
    return INITIAL_GLOBAL_FILTER_STATE.distributor
  }
  return value.startsWith("other:") ? value : value.toLowerCase()
}

export function distributor_valid_for_choices(
  distributor: Option<string>,
  choices: { value: string }[]
): boolean {
  if (!distributor) {
    return true
  }
  return choices.some(choice => choice.value === distributor)
}

/** Analytics endpoints only support invoice/check; fall back when posting or deposit is selected. */
export function analytics_date_kind(dateKind: DateKind): Exclude<DateKind, "posting" | "deposit"> {
  return dateKind === "posting" || dateKind === "deposit" ? "check" : dateKind
}

export const GlobalFilterState = entity(INITIAL_GLOBAL_FILTER_STATE, [persistence("global_filter", { deserializeFn })])

if (import.meta.vitest) {
  describe("distributor_valid_for_choices", () => {
    it("accepts null (all customers)", () => {
      expect(distributor_valid_for_choices(null, [{ value: "kehe" }])).toBe(true)
    })

    it("accepts distributor in choices", () => {
      expect(distributor_valid_for_choices("kehe", [{ value: "kehe" }, { value: "unfi" }])).toBe(true)
    })

    it("rejects distributor not in choices", () => {
      expect(distributor_valid_for_choices("kehe", [{ value: "unfi" }])).toBe(false)
    })
  })

  describe("saved_distributor", () => {
    it("keeps the case of an Other name, which the server matches exactly", () => {
      expect(saved_distributor("other:Grocery Outlet")).toBe("other:Grocery Outlet")
    })

    it("lowercases a source and drops the legacy all option", () => {
      expect(saved_distributor("KeHE")).toBe("kehe")
      expect(saved_distributor("All Distributors")).toBe(INITIAL_GLOBAL_FILTER_STATE.distributor)
      expect(saved_distributor(null)).toBe(INITIAL_GLOBAL_FILTER_STATE.distributor)
    })
  })

  describe("analytics_date_kind", () => {
    it("falls back posting to check", () => {
      expect(analytics_date_kind("posting")).toBe("check")
    })

    it("passes through invoice and check", () => {
      expect(analytics_date_kind("invoice")).toBe("invoice")
      expect(analytics_date_kind("check")).toBe("check")
    })
  })
}
